import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { base44 } from "api/apiClient";
import Layout from "../components/Layout";
import CarloAgent from "./CarloAgent";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "components/ui/button";
import { Alert, AlertDescription } from "components/ui/alert";
import { Label } from "components/ui/label";
import { Input } from "components/ui/input";
import { Checkbox } from "components/ui/checkbox";
import POSIntegrationsSection from "../components/POSIntegrationsSection";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { DEFAULT_MENU_CATEGORIES, normalizeMenuCategories, createPageUrl, parseLocaleNumber } from "utils";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  MapPin,
  MessageSquare,
  Navigation,
  Plug,
  Search,
  Store,
} from "lucide-react";

const VISIBLE_POS_PROVIDERS = ["sumup", "lightspeed", "orderbird"];
const WEEKDAY_OPTIONS = [
  { value: "Montag", labelKey: "monday", fallback: "Monday" },
  { value: "Dienstag", labelKey: "tuesday", fallback: "Tuesday" },
  { value: "Mittwoch", labelKey: "wednesday", fallback: "Wednesday" },
  { value: "Donnerstag", labelKey: "thursday", fallback: "Thursday" },
  { value: "Freitag", labelKey: "friday", fallback: "Friday" },
  { value: "Samstag", labelKey: "saturday", fallback: "Saturday" },
  { value: "Sonntag", labelKey: "sunday", fallback: "Sunday" },
];
const DEFAULT_OPENING_DAYS = WEEKDAY_OPTIONS.slice(0, 6).map((day) => day.value);
const DEFAULT_PRODUCTIVE_MINUTES_PER_STAFF_SHIFT = 240;
const DEFAULT_FORECAST_EFFORT_MINUTES = {
  Modifier: 0,
  Drink: 1.5,
  "Grab and Go": 0.5,
  Pastry: 1,
  "Simple Food": 3,
  "Main Plate": 8,
  "Batch Prep": 0,
  Other: 2,
};
const DAY_ALIASES = {
  monday: "Montag",
  montag: "Montag",
  tuesday: "Dienstag",
  dienstag: "Dienstag",
  wednesday: "Mittwoch",
  mittwoch: "Mittwoch",
  thursday: "Donnerstag",
  donnerstag: "Donnerstag",
  friday: "Freitag",
  freitag: "Freitag",
  saturday: "Samstag",
  samstag: "Samstag",
  sunday: "Sonntag",
  sonntag: "Sonntag",
};

const canonicalDay = (day) => DAY_ALIASES[String(day || "").trim().toLowerCase()] || day;

const normalizeOpeningDays = (days, fallback = DEFAULT_OPENING_DAYS) => {
  const source = Array.isArray(days) && days.length > 0 ? days : fallback;
  return Array.from(new Set(source.map(canonicalDay).filter(Boolean)));
};

const hasCoordinates = (restaurant) => (
  restaurant?.latitude !== null
  && restaurant?.latitude !== undefined
  && restaurant?.latitude !== ""
  && restaurant?.longitude !== null
  && restaurant?.longitude !== undefined
  && restaurant?.longitude !== ""
);

const normalizeEffortMinutesByClass = (value) => {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  return Object.fromEntries(
    Object.entries(DEFAULT_FORECAST_EFFORT_MINUTES).map(([effortClass, fallback]) => [
      effortClass,
      source[effortClass] ?? fallback,
    ])
  );
};

const normalizeForecastEffortSettings = (data) => {
  const productiveMinutes = parseLocaleNumber(data.productive_minutes_per_staff_shift);
  const effortMinutes = normalizeEffortMinutesByClass(data.effort_minutes_by_class);

  return {
    productive_minutes_per_staff_shift: Number.isFinite(productiveMinutes) && productiveMinutes > 0
      ? Math.round(productiveMinutes)
      : DEFAULT_PRODUCTIVE_MINUTES_PER_STAFF_SHIFT,
    effort_minutes_by_class: Object.fromEntries(
      Object.entries(DEFAULT_FORECAST_EFFORT_MINUTES).map(([effortClass, fallback]) => {
        const minutes = parseLocaleNumber(effortMinutes[effortClass]);
        return [effortClass, Number.isFinite(minutes) && minutes >= 0 ? minutes : fallback];
      })
    ),
  };
};

const buildDefaultRestaurantForm = () => ({
  name: "",
  location_label: "",
  latitude: "",
  longitude: "",
  opening_days: DEFAULT_OPENING_DAYS,
  days_per_week: DEFAULT_OPENING_DAYS.length,
  lunch_service: true,
  dinner_service: true,
  lunch_start: "11:30",
  lunch_end: "14:30",
  dinner_start: "17:30",
  dinner_end: "22:00",
  menu_categories: DEFAULT_MENU_CATEGORIES,
  productive_minutes_per_staff_shift: DEFAULT_PRODUCTIVE_MINUTES_PER_STAFF_SHIFT,
  effort_minutes_by_class: normalizeEffortMinutesByClass(),
});

const restaurantToForm = (restaurant) => {
  const openingDays = normalizeOpeningDays(restaurant?.opening_days);
  return {
    ...buildDefaultRestaurantForm(),
    name: restaurant?.name || "",
    location_label: restaurant?.location_label || "",
    latitude: restaurant?.latitude ?? "",
    longitude: restaurant?.longitude ?? "",
    opening_days: openingDays,
    days_per_week: openingDays.length,
    lunch_service: restaurant?.lunch_service !== undefined ? restaurant.lunch_service : true,
    dinner_service: restaurant?.dinner_service !== undefined ? restaurant.dinner_service : true,
    lunch_start: restaurant?.lunch_start || "11:30",
    lunch_end: restaurant?.lunch_end || "14:30",
    dinner_start: restaurant?.dinner_start || "17:30",
    dinner_end: restaurant?.dinner_end || "22:00",
    menu_categories: DEFAULT_MENU_CATEGORIES,
    productive_minutes_per_staff_shift: restaurant?.productive_minutes_per_staff_shift || DEFAULT_PRODUCTIVE_MINUTES_PER_STAFF_SHIFT,
    effort_minutes_by_class: normalizeEffortMinutesByClass(restaurant?.effort_minutes_by_class),
  };
};

const buildMapEmbedUrl = (latValue, lonValue) => {
  const lat = parseLocaleNumber(latValue);
  const lon = parseLocaleNumber(lonValue);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return "";
  const latDelta = 0.025;
  const lonDelta = 0.04;
  const bbox = [
    (lon - lonDelta).toFixed(6),
    (lat - latDelta).toFixed(6),
    (lon + lonDelta).toFixed(6),
    (lat + latDelta).toFixed(6),
  ].join(",");
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat.toFixed(6)},${lon.toFixed(6)}`;
};

const formatCoordinate = (value) => {
  const parsed = parseLocaleNumber(value);
  return Number.isFinite(parsed) ? parsed.toFixed(6) : "";
};

const SETTINGS_TABS = ["restaurants", "pos", "onboarding-chat"];

export default function Setup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { fetchUser } = useAuth();
  const requestedTab = searchParams.get("tab");
  const activeTab = SETTINGS_TABS.includes(requestedTab) ? requestedTab : "restaurants";
  const handleTabChange = (tab) => {
    setSearchParams(tab === "restaurants" ? {} : { tab }, { replace: true });
  };
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(null);
  const [restaurantData, setRestaurantData] = useState(() => buildDefaultRestaurantForm());
  const [saveError, setSaveError] = useState(null);
  const [savingRestaurant, setSavingRestaurant] = useState(false);
  const [locationQuery, setLocationQuery] = useState("");
  const [locationResults, setLocationResults] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [saveNotice, setSaveNotice] = useState("");
  const [visiblePosConnected, setVisiblePosConnected] = useState(null);

  const daysOfWeek = useMemo(() => WEEKDAY_OPTIONS.map((day) => ({
    ...day,
    label: t(`setup.${day.labelKey}`, day.fallback),
  })), [t]);

  const {
    data: restaurants = [],
    isLoading: restaurantsLoading,
    refetch: refetchRestaurants,
  } = useQuery({
    queryKey: ["restaurants", "setup-all"],
    queryFn: () => base44.entities.Restaurant.list("-id", { all: true }),
  });

  const { data: integrations = [] } = useQuery({
    queryKey: ["pos-integrations", "setup"],
    queryFn: () => base44.pos.getIntegrations(),
    initialData: [],
  });

  const selectedRestaurant = selectedRestaurantId === "new"
    ? null
    : restaurants.find((restaurant) => restaurant.id === Number(selectedRestaurantId));

  const restaurantReady = restaurants.length > 0 && restaurants.every(hasCoordinates);
  const queriedPosReady = integrations.some((integration) =>
    VISIBLE_POS_PROVIDERS.includes(integration.provider) && integration.is_active
  );
  const posReady = visiblePosConnected ?? queriedPosReady;

  useEffect(() => {
    if (selectedRestaurantId !== null || restaurantsLoading) return;
    setSelectedRestaurantId(restaurants.length > 0 ? restaurants[0].id : "new");
  }, [restaurants, restaurantsLoading, selectedRestaurantId]);

  useEffect(() => {
    if (selectedRestaurantId === "new") {
      setRestaurantData(buildDefaultRestaurantForm());
      setLocationQuery("");
      setLocationResults([]);
      setSaveError(null);
      setSaveNotice("");
      return;
    }
    if (selectedRestaurant) {
      setRestaurantData(restaurantToForm(selectedRestaurant));
      setLocationQuery(selectedRestaurant.location_label || selectedRestaurant.name || "");
      setLocationResults([]);
      setSaveError(null);
      setSaveNotice("");
    }
  }, [selectedRestaurantId, selectedRestaurant]);

  const handlePosStatusChange = useCallback(({ connected }) => {
    setVisiblePosConnected(connected);
  }, []);

  const toggleDay = (dayValue) => {
    setRestaurantData((prev) => {
      const currentOpeningDays = normalizeOpeningDays(prev.opening_days, []);
      const openingDays = currentOpeningDays.includes(dayValue)
        ? currentOpeningDays.filter((candidate) => candidate !== dayValue)
        : [...currentOpeningDays, dayValue];
      return {
        ...prev,
        opening_days: openingDays,
        days_per_week: openingDays.length,
      };
    });
  };

  const searchLocations = async (event) => {
    event?.preventDefault();
    const query = locationQuery.trim();
    if (!query) return;

    setLocationLoading(true);
    setLocationError("");
    try {
      const params = new URLSearchParams({
        format: "jsonv2",
        limit: "6",
        addressdetails: "1",
        q: query,
      });
      const response = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`);
      if (!response.ok) throw new Error(`Location search failed (${response.status})`);
      const data = await response.json();
      setLocationResults(Array.isArray(data) ? data : []);
      if (!Array.isArray(data) || data.length === 0) {
        setLocationError(t("setupOnboarding.noLocationResults", "No matching places found."));
      }
    } catch (error) {
      setLocationError(error.message || t("setupOnboarding.locationSearchFailed", "Location search failed."));
    } finally {
      setLocationLoading(false);
    }
  };

  const selectLocation = (result) => {
    const lat = formatCoordinate(result.lat);
    const lon = formatCoordinate(result.lon);
    setRestaurantData((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lon,
      location_label: result.display_name || locationQuery,
    }));
    setLocationQuery(result.display_name || locationQuery);
    setLocationResults([]);
    setLocationError("");
    setSaveNotice("");
  };

  const saveRestaurantSettings = async () => {
    setSaveError(null);
    setSaveNotice("");
    const menuCategories = normalizeMenuCategories(DEFAULT_MENU_CATEGORIES);
    if (!restaurantData.name.trim()) {
      setSaveError(t("setupOnboarding.restaurantNameRequired", "Restaurant name is required."));
      return null;
    }
    if (!restaurantData.latitude || !restaurantData.longitude) {
      setSaveError(t("setupOnboarding.restaurantLocationRequired", "Pick a map location before continuing."));
      return null;
    }
    if (menuCategories.length === 0) {
      setSaveError(t("setup.menuCategoriesRequired", "At least one menu category is required."));
      return null;
    }

    const openingDays = normalizeOpeningDays(restaurantData.opening_days, []);
    const payload = {
      ...restaurantData,
      ...normalizeForecastEffortSettings(restaurantData),
      name: restaurantData.name.trim(),
      location_label: restaurantData.location_label.trim(),
      latitude: restaurantData.latitude || null,
      longitude: restaurantData.longitude || null,
      opening_days: openingDays,
      days_per_week: openingDays.length,
      menu_categories: menuCategories,
    };

    setSavingRestaurant(true);
    try {
      const saved = selectedRestaurant
        ? await base44.entities.Restaurant.update(selectedRestaurant.id, payload)
        : await base44.entities.Restaurant.create(payload);
      if (!selectedRestaurant) {
        localStorage.setItem("current_restaurant_id", String(saved.id));
        setSelectedRestaurantId(saved.id);
      }
      await fetchUser();
      await queryClient.invalidateQueries({ queryKey: ["restaurants"] });
      await queryClient.invalidateQueries({ queryKey: ["restaurant"] });
      await refetchRestaurants();
      setSaveNotice(t("setupOnboarding.restaurantSaved", "Restaurant location saved."));
      return saved;
    } catch (error) {
      console.error("Error saving restaurant settings:", error);
      setSaveError(error?.response?.data?.detail || error.message || t("setup.saveError", "Failed to save settings"));
      return null;
    } finally {
      setSavingRestaurant(false);
    }
  };

  const mapUrl = buildMapEmbedUrl(restaurantData.latitude, restaurantData.longitude);

  const renderRestaurantStep = () => (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-950">
            {t("setupOnboarding.restaurantsTitle", "Restaurants and locations")}
          </h2>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
            {t("setupOnboarding.restaurantsSubtitle", "Each restaurant needs coordinates so weather forecasting and local planning use the correct place.")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            className="bg-slate-900 text-white hover:bg-slate-800"
            onClick={saveRestaurantSettings}
            disabled={savingRestaurant}
          >
            {savingRestaurant ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
            {t("setupOnboarding.saveRestaurant", "Save restaurant")}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setSelectedRestaurantId("new")}
          >
            <Store className="mr-2 h-4 w-4" />
            {t("setupOnboarding.addRestaurant", "Add restaurant")}
          </Button>
        </div>
      </div>

      {restaurantsLoading && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("setupOnboarding.loadingRestaurants", "Loading restaurants")}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        <div className="space-y-3">
          {restaurants.map((restaurant) => {
            const isSelected = Number(selectedRestaurantId) === restaurant.id;
            const rowHasCoordinates = isSelected ? hasCoordinates(restaurantData) : hasCoordinates(restaurant);
            const rowLocation = isSelected
              ? restaurantData.location_label || (rowHasCoordinates ? `${restaurantData.latitude}, ${restaurantData.longitude}` : "")
              : restaurant.location_label;

            return (
              <button
                key={restaurant.id}
                type="button"
                onClick={() => setSelectedRestaurantId(restaurant.id)}
                className={`w-full rounded-lg border px-4 py-3 text-left transition ${
                  isSelected
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="truncate text-sm font-semibold">{isSelected ? restaurantData.name || restaurant.name : restaurant.name}</span>
                  {rowHasCoordinates ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                  )}
                </div>
                <p className={`mt-1 line-clamp-2 text-xs ${
                  isSelected ? "text-white/70" : "text-slate-500"
                }`}>
                  {rowLocation || t("setupOnboarding.noLocationSaved", "No location saved")}
                </p>
              </button>
            );
          })}

          {selectedRestaurantId === "new" && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              {t("setupOnboarding.newRestaurantSelected", "New restaurant")}
            </div>
          )}
        </div>

        <div className="space-y-5">
          <div className="grid gap-4 2xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.95fr)]">
            <div className="min-w-0 space-y-4">
              <div>
                <Label htmlFor="restaurant_name">{t("setup.restaurantName", "Restaurant name")}</Label>
                <Input
                  id="restaurant_name"
                  value={restaurantData.name}
                  onChange={(event) => setRestaurantData((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder={t("setup.restaurantNamePlaceholder", "Carlo Pizza")}
                />
              </div>

              <form className="space-y-3" onSubmit={searchLocations}>
                <Label htmlFor="restaurant_location">{t("setupOnboarding.locationSearch", "Location")}</Label>
                <div className="flex gap-2">
                  <Input
                    id="restaurant_location"
                    value={locationQuery}
                    onChange={(event) => setLocationQuery(event.target.value)}
                    placeholder={t("setupOnboarding.locationPlaceholder", "Munich, Germany")}
                  />
                  <Button type="submit" variant="outline" disabled={locationLoading}>
                    {locationLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>
              </form>

              {locationError && (
                <Alert className="border-amber-200 bg-amber-50 text-amber-800">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{locationError}</AlertDescription>
                </Alert>
              )}

              {locationResults.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                  {locationResults.map((result) => (
                    <button
                      key={`${result.place_id}-${result.lat}-${result.lon}`}
                      type="button"
                      onClick={() => selectLocation(result)}
                      className="flex w-full items-start gap-3 border-b border-slate-100 px-4 py-3 text-left last:border-b-0 hover:bg-slate-50"
                    >
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                      <span>
                        <span className="block text-sm font-medium text-slate-800">
                          {result.name || result.display_name?.split(",")[0] || locationQuery}
                        </span>
                        <span className="mt-0.5 block text-xs leading-5 text-slate-500">
                          {result.display_name}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="restaurant_latitude">{t("setupOnboarding.latitude", "Latitude")}</Label>
                  <Input
                    id="restaurant_latitude"
                    value={restaurantData.latitude}
                    onChange={(event) => setRestaurantData((prev) => ({ ...prev, latitude: event.target.value }))}
                    placeholder="48.137154"
                  />
                </div>
                <div>
                  <Label htmlFor="restaurant_longitude">{t("setupOnboarding.longitude", "Longitude")}</Label>
                  <Input
                    id="restaurant_longitude"
                    value={restaurantData.longitude}
                    onChange={(event) => setRestaurantData((prev) => ({ ...prev, longitude: event.target.value }))}
                    placeholder="11.576124"
                  />
                </div>
              </div>

              {hasCoordinates(restaurantData) && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  <div className="flex min-w-0 items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {restaurantData.location_label || `${restaurantData.latitude}, ${restaurantData.longitude}`}
                    </span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="bg-emerald-700 text-white hover:bg-emerald-800"
                    onClick={saveRestaurantSettings}
                    disabled={savingRestaurant}
                  >
                    {savingRestaurant ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                    {t("setupOnboarding.saveLocation", "Save location")}
                  </Button>
                </div>
              )}
            </div>

            <div className="min-h-[320px] min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
              {mapUrl ? (
                <iframe
                  title={restaurantData.location_label || restaurantData.name || t("setupOnboarding.restaurantMap", "Restaurant map")}
                  src={mapUrl}
                  className="h-full min-h-[320px] w-full"
                />
              ) : (
                <div className="flex h-full min-h-[320px] flex-col items-center justify-center p-6 text-center text-slate-500">
                  <Navigation className="h-8 w-8" />
                  <p className="mt-3 text-sm">
                    {t("setupOnboarding.mapPlaceholder", "Search and select a place to save map coordinates.")}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-3">
                <Label className="mb-1 block">{t("setup.menuCategories", "Menu categories")}</Label>
                <p className="text-sm text-slate-500">
                  {t("setup.menuCategoriesDesc", "Used for product grouping and POS imports.")}
                </p>
              </div>
              <div className="space-y-2">
                {DEFAULT_MENU_CATEGORIES.map((category) => (
                  <div key={category} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700">
                    {category}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <Label className="mb-3 block">{t("setup.openingDays", "Opening days")}</Label>
              <div className="grid grid-cols-2 gap-3">
                {daysOfWeek.map((day) => (
                  <div key={day.value} className="flex items-center gap-2">
                    <Checkbox
                      id={`day-${day.value}`}
                      checked={normalizeOpeningDays(restaurantData.opening_days, []).includes(day.value)}
                      onCheckedChange={() => toggleDay(day.value)}
                    />
                    <Label htmlFor={`day-${day.value}`} className="cursor-pointer text-sm font-normal">
                      {day.label}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-sm text-slate-500">
                {restaurantData.days_per_week} {t("setup.daysSelected", "days selected")}
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-3 flex items-center gap-2">
                <Checkbox
                  id="lunch"
                  checked={restaurantData.lunch_service}
                  onCheckedChange={(checked) => setRestaurantData((prev) => ({ ...prev, lunch_service: Boolean(checked) }))}
                />
                <Label htmlFor="lunch" className="cursor-pointer font-medium">
                  {t("setup.lunchService", "Lunch service")}
                </Label>
              </div>
              {restaurantData.lunch_service && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="lunch_start" className="text-sm">{t("setup.from", "From")}</Label>
                    <Input
                      id="lunch_start"
                      type="time"
                      value={restaurantData.lunch_start}
                      onChange={(event) => setRestaurantData((prev) => ({ ...prev, lunch_start: event.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="lunch_end" className="text-sm">{t("setup.to", "To")}</Label>
                    <Input
                      id="lunch_end"
                      type="time"
                      value={restaurantData.lunch_end}
                      onChange={(event) => setRestaurantData((prev) => ({ ...prev, lunch_end: event.target.value }))}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-3 flex items-center gap-2">
                <Checkbox
                  id="dinner"
                  checked={restaurantData.dinner_service}
                  onCheckedChange={(checked) => setRestaurantData((prev) => ({ ...prev, dinner_service: Boolean(checked) }))}
                />
                <Label htmlFor="dinner" className="cursor-pointer font-medium">
                  {t("setup.dinnerService", "Dinner service")}
                </Label>
              </div>
              {restaurantData.dinner_service && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="dinner_start" className="text-sm">{t("setup.from", "From")}</Label>
                    <Input
                      id="dinner_start"
                      type="time"
                      value={restaurantData.dinner_start}
                      onChange={(event) => setRestaurantData((prev) => ({ ...prev, dinner_start: event.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="dinner_end" className="text-sm">{t("setup.to", "To")}</Label>
                    <Input
                      id="dinner_end"
                      type="time"
                      value={restaurantData.dinner_end}
                      onChange={(event) => setRestaurantData((prev) => ({ ...prev, dinner_end: event.target.value }))}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600">
                <Clock className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">
                  {t("setup.forecastEffortSettings", "Forecast effort settings")}
                </h3>
                <p className="mt-1 text-sm leading-5 text-slate-500">
                  {t("setup.forecastEffortSettingsDesc", "Used for per-item forecast effort and shift staffing suggestions.")}
                </p>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
              <div>
                <Label htmlFor="productive_minutes_per_staff_shift" className="text-sm">
                  {t("setup.productiveMinutesPerShift", "Productive minutes per staff shift")}
                </Label>
                <Input
                  id="productive_minutes_per_staff_shift"
                  type="number"
                  min="1"
                  step="1"
                  value={restaurantData.productive_minutes_per_staff_shift}
                  onChange={(event) => setRestaurantData((prev) => ({
                    ...prev,
                    productive_minutes_per_staff_shift: event.target.value,
                  }))}
                  className="mt-1"
                />
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {Object.entries(DEFAULT_FORECAST_EFFORT_MINUTES).map(([effortClass]) => (
                  <div key={effortClass}>
                    <Label htmlFor={`effort-${effortClass}`} className="text-sm">
                      {effortClass}
                    </Label>
                    <Input
                      id={`effort-${effortClass}`}
                      type="number"
                      min="0"
                      step="0.25"
                      value={restaurantData.effort_minutes_by_class?.[effortClass] ?? ""}
                      onChange={(event) => setRestaurantData((prev) => ({
                        ...prev,
                        effort_minutes_by_class: {
                          ...normalizeEffortMinutesByClass(prev.effort_minutes_by_class),
                          [effortClass]: event.target.value,
                        },
                      }))}
                      className="mt-1"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {saveError && (
            <Alert className="border-red-200 bg-red-50 text-red-700">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{saveError}</AlertDescription>
            </Alert>
          )}
          {saveNotice && (
            <Alert className="border-emerald-200 bg-emerald-50 text-emerald-800">
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>{saveNotice}</AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );

  const renderPosStep = () => (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-950">
            {t("setupOnboarding.posTitle", "Connect the POS system")}
          </h2>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
            {t("setupOnboarding.posSubtitle", "POS sales and menu data let Carlo compare revenue, product demand, and ingredient usage.")}
          </p>
        </div>
        {posReady && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {t("pos.connected", "Connected")}
          </span>
        )}
      </div>
      <POSIntegrationsSection onStatusChange={handlePosStatusChange} />
    </div>
  );

  const page = (
    <div className="mx-auto max-w-7xl px-0 md:px-2 pb-6 sm:pb-10">
      <div className="mb-5 sm:mb-7 flex flex-col gap-3 sm:gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-normal text-slate-950">
            {t("settings.title", "Settings")}
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            {t("settings.subtitle", "Manage restaurant locations, opening hours, forecast effort settings, POS connections, and onboarding conversations.")}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate(createPageUrl("Dashboard"))}>
          {t("setup.goToDashboard", "Go to dashboard")}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="bg-white shadow-sm">
          <TabsTrigger value="restaurants">
            <MapPin className="mr-2 h-4 w-4" />
            {t("settings.restaurantsTab", "Restaurants")}
            {restaurantReady && <CheckCircle2 className="ml-2 h-3.5 w-3.5 text-emerald-600" />}
          </TabsTrigger>
          <TabsTrigger value="pos">
            <Plug className="mr-2 h-4 w-4" />
            {t("settings.posTab", "POS system")}
            {posReady && <CheckCircle2 className="ml-2 h-3.5 w-3.5 text-emerald-600" />}
          </TabsTrigger>
          <TabsTrigger value="onboarding-chat">
            <MessageSquare className="mr-2 h-4 w-4" />
            {t("settings.onboardingChatTab", "Onboarding chat")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="restaurants">
          <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 sm:p-5 md:p-6">
            {renderRestaurantStep()}
          </div>
        </TabsContent>

        <TabsContent value="pos">
          <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 sm:p-5 md:p-6">
            {renderPosStep()}
          </div>
        </TabsContent>

        <TabsContent value="onboarding-chat">
          <div className="min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white">
            <div className="h-[calc(100dvh-15rem)] min-h-[520px]">
              <CarloAgent embedded onboardingChatsOnly />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );

  return (
    <Layout>{page}</Layout>
  );
}
